import { useEffect, useRef } from "react";

const BACK_DISMISS_STATE_KEY = "carrierBackDismissOverlay";

interface BackDismissRegistration {
  token: symbol;
  canDismiss: () => boolean;
  dismiss: () => void;
}

const backDismissStack: BackDismissRegistration[] = [];
let backDismissMarkerPresent = false;
let programmaticBacks = 0;
let releaseGeneration = 0;
let listeningForBack = false;

function historyState(): Record<string, unknown> {
  return window.history.state && typeof window.history.state === "object"
    ? window.history.state
    : {};
}

function pushBackDismissMarker() {
  window.history.pushState(
    { ...historyState(), [BACK_DISMISS_STATE_KEY]: true },
    ""
  );
  backDismissMarkerPresent = true;
}

function handleBrowserBack() {
  if (programmaticBacks > 0) {
    programmaticBacks -= 1;
    backDismissMarkerPresent = historyState()[BACK_DISMISS_STATE_KEY] === true;
    if (backDismissStack.length > 0 && !backDismissMarkerPresent) {
      pushBackDismissMarker();
    }
    return;
  }

  const active = backDismissStack.at(-1);
  if (!active || !backDismissMarkerPresent) return;
  if (!active.canDismiss()) {
    pushBackDismissMarker();
    return;
  }

  backDismissMarkerPresent = false;
  active.dismiss();
}

function ensureBackListener() {
  if (listeningForBack || typeof window === "undefined") return;
  window.addEventListener("popstate", handleBrowserBack);
  listeningForBack = true;
}

function registerBackDismiss(registration: BackDismissRegistration) {
  ensureBackListener();
  releaseGeneration += 1;
  backDismissStack.push(registration);
  if (!backDismissMarkerPresent) pushBackDismissMarker();
}

function unregisterBackDismiss(token: symbol) {
  const index = backDismissStack.findIndex((entry) => entry.token === token);
  if (index >= 0) backDismissStack.splice(index, 1);

  if (backDismissStack.length > 0) {
    if (!backDismissMarkerPresent) pushBackDismissMarker();
    return;
  }

  const generation = ++releaseGeneration;
  queueMicrotask(() => {
    if (generation !== releaseGeneration || backDismissStack.length > 0) {
      return;
    }
    if (
      backDismissMarkerPresent &&
      historyState()[BACK_DISMISS_STATE_KEY] === true
    ) {
      backDismissMarkerPresent = false;
      programmaticBacks += 1;
      window.history.back();
      return;
    }
    backDismissMarkerPresent = false;
  });
}

export function useBrowserBackDismiss({
  open,
  enabled = true,
  canDismiss = true,
  onDismiss,
}: {
  open: boolean;
  enabled?: boolean;
  canDismiss?: boolean;
  onDismiss: () => void;
}) {
  const onDismissRef = useRef(onDismiss);
  const canDismissRef = useRef(canDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
    canDismissRef.current = canDismiss;
  }, [canDismiss, onDismiss]);

  useEffect(() => {
    if (!enabled || !open || typeof window === "undefined") return;
    const registration: BackDismissRegistration = {
      token: Symbol("back-dismiss-overlay"),
      canDismiss: () => canDismissRef.current,
      dismiss: () => onDismissRef.current(),
    };
    registerBackDismiss(registration);
    return () => unregisterBackDismiss(registration.token);
  }, [enabled, open]);
}
